import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { generateId, getItem } from 'lib/storage';
import {
  fetchCryptoRates,
  fetchState,
  saveCategories,
  saveSettings,
  saveTransactions,
} from 'lib/api';
import {
  calcTotals,
  filterTransactions,
  groupByMonth,
  groupByCategory,
  getBalanceOverTime,
  currentMonthKey,
  availableMonths,
} from 'lib/finance';
import {
  AppSettings,
  Category,
  convertWalletToBdt,
  CryptoRates,
  CurrencyCode,
  DEFAULT_CATEGORIES,
  DEFAULT_CURRENCY,
  DEFAULT_USDT_PRICE_BDT,
  DEFAULT_USER_ID,
  DEFAULT_WALLETS,
  FinanceTotals,
  SavedWallet,
  STORAGE_KEYS,
  Transaction,
  TransactionFilters,
  TransactionType,
  getCurrencySymbol,
} from 'types/finance';

interface FinanceContextValue {
  ready: boolean;
  storageError: string | null;
  transactions: Transaction[];
  categories: Category[];
  filters: TransactionFilters;
  filteredTransactions: Transaction[];
  totals: FinanceTotals;
  monthlyData: ReturnType<typeof groupByMonth>;
  expenseByCategory: ReturnType<typeof groupByCategory>;
  incomeByCategory: ReturnType<typeof groupByCategory>;
  balanceOverTime: ReturnType<typeof getBalanceOverTime>;
  currency: CurrencyCode;
  currencySymbol: string;
  usdtPriceBdt: number;
  wallets: SavedWallet[];
  cryptoRates: CryptoRates;
  setCurrency: (currency: CurrencyCode) => void;
  setUsdtPriceBdt: (price: number) => void;
  addWallet: (wallet: Omit<SavedWallet, 'id'>) => void;
  updateWallet: (id: string, data: Partial<SavedWallet>) => void;
  deleteWallet: (id: string) => void;
  setFilters: (filters: Partial<TransactionFilters>) => void;
  resetFilters: () => void;
  addTransaction: (
    data: Omit<Transaction, 'id' | 'userId' | 'createdAt'>
  ) => void;
  addTransactions: (
    items: Array<Omit<Transaction, 'id' | 'userId' | 'createdAt'>>
  ) => { added: number; skipped: number };
  updateTransaction: (id: string, data: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  toggleMarked: (id: string) => void;
  deleteMarked: () => number;
  addCategory: (name: string, type: TransactionType, color: string) => void;
  updateCategory: (id: string, name: string) => void;
  deleteCategory: (id: string) => string | null;
  getCategoriesByType: (type: TransactionType) => Category[];
  getDisplayAmount: (tx: Transaction) => number;
  availableMonthKeys: string[];
}

const defaultFilters: TransactionFilters = {
  dateFrom: '',
  dateTo: '',
  categoryId: '',
  type: 'all',
  month: currentMonthKey(),
};

const defaultRates: CryptoRates = { USDT: 1, TRX: 0, BNB: 0, ETH: 0 };

const FinanceContext = createContext<FinanceContextValue | null>(null);

function seedCategories(userId: string): Category[] {
  return DEFAULT_CATEGORIES.map((cat) => ({
    ...cat,
    id: generateId(),
    userId,
  }));
}

function readLegacyLocalStorage(userId: string): {
  transactions: Transaction[];
  categories: Category[];
  currency: CurrencyCode;
} {
  const settings = getItem<{ currency?: CurrencyCode }>(
    STORAGE_KEYS.settings(userId),
    {}
  );
  return {
    transactions: getItem<Transaction[]>(STORAGE_KEYS.transactions(userId), []),
    categories: getItem<Category[]>(STORAGE_KEYS.categories(userId), []),
    currency: settings.currency ?? DEFAULT_CURRENCY,
  };
}

function recalculateWalletAmounts(
  txs: Transaction[],
  rates: CryptoRates,
  usdtPriceBdt: number
): Transaction[] {
  return txs.map((tx) => {
    if (tx.source !== 'wallet' || tx.tokenAmount == null) return tx;
    const { amountUsdt, amountBdt } = convertWalletToBdt(
      tx.tokenSymbol,
      tx.tokenAmount,
      rates,
      usdtPriceBdt
    );
    return { ...tx, amountUsdt, amount: amountBdt };
  });
}

export function FinanceProvider({ children }: { children: ReactNode }) {
  const userId = DEFAULT_USER_ID;
  const [ready, setReady] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filters, setFiltersState] = useState<TransactionFilters>(defaultFilters);
  const [currency, setCurrencyState] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [usdtPriceBdt, setUsdtPriceBdtState] = useState(DEFAULT_USDT_PRICE_BDT);
  const [wallets, setWallets] = useState<SavedWallet[]>(DEFAULT_WALLETS);
  const [cryptoRates, setCryptoRates] = useState<CryptoRates>(defaultRates);

  const persistSettings = useCallback(
    async (patch: Partial<AppSettings>) => {
      const next = await saveSettings(patch);
      setCurrencyState(next.currency);
      setUsdtPriceBdtState(next.usdtPriceBdt);
      setWallets(next.wallets);
      return next;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        let state = await fetchState();
        const rates = await fetchCryptoRates().catch(() => defaultRates);
        const legacy = readLegacyLocalStorage(userId);
        const dbEmpty =
          state.transactions.length === 0 && state.categories.length === 0;
        const hasLegacy =
          legacy.transactions.length > 0 || legacy.categories.length > 0;

        if (dbEmpty && hasLegacy) {
          const cats =
            legacy.categories.length > 0
              ? legacy.categories
              : seedCategories(userId);
          state = {
            transactions: legacy.transactions,
            categories: cats,
            settings: {
              currency: legacy.currency,
              usdtPriceBdt: DEFAULT_USDT_PRICE_BDT,
              wallets: DEFAULT_WALLETS,
            },
          };
          await saveTransactions(state.transactions);
          await saveCategories(state.categories);
          await saveSettings(state.settings);
        } else if (state.categories.length === 0) {
          const cats = seedCategories(userId);
          state = { ...state, categories: cats };
          await saveCategories(cats);
        }

        if (cancelled) return;
        const price = state.settings.usdtPriceBdt ?? DEFAULT_USDT_PRICE_BDT;
        const txs = recalculateWalletAmounts(state.transactions, rates, price);
        setTransactions(txs);
        setCategories(state.categories);
        setCurrencyState(state.settings.currency || DEFAULT_CURRENCY);
        setUsdtPriceBdtState(price);
        setWallets(state.settings.wallets?.length ? state.settings.wallets : DEFAULT_WALLETS);
        setCryptoRates(rates);
        setFiltersState(defaultFilters);
        setStorageError(null);
        if (txs !== state.transactions) {
          saveTransactions(txs).catch(() => undefined);
        }
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setStorageError(
          'Could not connect to the data API. Run `npm start` (starts API + app) and keep the API on port 4000.'
        );
        const legacy = readLegacyLocalStorage(userId);
        const cats =
          legacy.categories.length > 0
            ? legacy.categories
            : seedCategories(userId);
        setTransactions(legacy.transactions);
        setCategories(cats);
        setCurrencyState(legacy.currency);
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setCurrency = useCallback(
    (next: CurrencyCode) => {
      setCurrencyState(next);
      persistSettings({ currency: next }).catch((err) => console.error(err));
    },
    [persistSettings]
  );

  const setUsdtPriceBdt = useCallback(
    (price: number) => {
      const next = Number(price) || 0;
      setUsdtPriceBdtState(next);
      setTransactions((prev) => {
        const updated = recalculateWalletAmounts(prev, cryptoRates, next);
        saveTransactions(updated).catch((err) => console.error(err));
        return updated;
      });
      persistSettings({ usdtPriceBdt: next }).catch((err) => console.error(err));
    },
    [cryptoRates, persistSettings]
  );

  const addWallet = useCallback(
    (wallet: Omit<SavedWallet, 'id'>) => {
      const next: SavedWallet = {
        ...wallet,
        id: generateId(),
        address: wallet.address.trim(),
        label: wallet.label.trim() || wallet.chain.toUpperCase(),
      };
      setWallets((prev) => {
        const walletsNext = [...prev, next];
        persistSettings({ wallets: walletsNext }).catch((err) =>
          console.error(err)
        );
        return walletsNext;
      });
    },
    [persistSettings]
  );

  const updateWallet = useCallback(
    (id: string, data: Partial<SavedWallet>) => {
      setWallets((prev) => {
        const walletsNext = prev.map((w) =>
          w.id === id ? { ...w, ...data, address: (data.address ?? w.address).trim() } : w
        );
        persistSettings({ wallets: walletsNext }).catch((err) =>
          console.error(err)
        );
        return walletsNext;
      });
    },
    [persistSettings]
  );

  const deleteWallet = useCallback(
    (id: string) => {
      setWallets((prev) => {
        const walletsNext = prev.filter((w) => w.id !== id);
        persistSettings({ wallets: walletsNext }).catch((err) =>
          console.error(err)
        );
        return walletsNext;
      });
    },
    [persistSettings]
  );

  const currencySymbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  const getDisplayAmount = useCallback(
    (tx: Transaction) => {
      if (tx.source === 'wallet' && tx.tokenAmount != null) {
        return convertWalletToBdt(
          tx.tokenSymbol,
          tx.tokenAmount,
          cryptoRates,
          usdtPriceBdt
        ).amountBdt;
      }
      return tx.amount;
    },
    [cryptoRates, usdtPriceBdt]
  );

  const persistTransactions = useCallback((txs: Transaction[]) => {
    setTransactions(txs);
    saveTransactions(txs).catch((err) => console.error(err));
  }, []);

  const persistCategories = useCallback((cats: Category[]) => {
    setCategories(cats);
    saveCategories(cats).catch((err) => console.error(err));
  }, []);

  const setFilters = useCallback((partial: Partial<TransactionFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetFilters = useCallback(() => setFiltersState(defaultFilters), []);

  const addTransaction = useCallback(
    (data: Omit<Transaction, 'id' | 'userId' | 'createdAt'>) => {
      const tx: Transaction = {
        ...data,
        id: generateId(),
        userId,
        createdAt: new Date().toISOString(),
      };
      persistTransactions([tx, ...transactions]);
    },
    [userId, transactions, persistTransactions]
  );

  const addTransactions = useCallback(
    (items: Array<Omit<Transaction, 'id' | 'userId' | 'createdAt'>>) => {
      if (!items.length) return { added: 0, skipped: 0 };

      const existingRefs = new Set(
        transactions
          .map((tx) => (tx.reference || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const fingerprint = (tx: {
        reference?: string;
        date: string;
        type: string;
        amount: number;
        description: string;
      }) =>
        `${tx.date}|${tx.type}|${tx.amount}|${(tx.description || '').trim()}`;

      const existingFp = new Set(transactions.map((tx) => fingerprint(tx)));
      const fresh: typeof items = [];
      let skipped = 0;

      for (const item of items) {
        const ref = (item.reference || '').trim().toLowerCase();
        if (ref && existingRefs.has(ref)) {
          skipped += 1;
          continue;
        }
        const fp = fingerprint(item);
        if (!ref && existingFp.has(fp)) {
          skipped += 1;
          continue;
        }
        fresh.push(item);
        if (ref) existingRefs.add(ref);
        existingFp.add(fp);
      }

      if (!fresh.length) return { added: 0, skipped };

      const createdAt = new Date().toISOString();
      const txs: Transaction[] = fresh.map((data) => ({
        ...data,
        id: generateId(),
        userId,
        createdAt,
      }));
      persistTransactions([...txs, ...transactions]);
      return { added: txs.length, skipped };
    },
    [userId, transactions, persistTransactions]
  );

  const updateTransaction = useCallback(
    (id: string, data: Partial<Transaction>) => {
      persistTransactions(
        transactions.map((tx) => (tx.id === id ? { ...tx, ...data } : tx))
      );
    },
    [transactions, persistTransactions]
  );

  const deleteTransaction = useCallback(
    (id: string) => {
      persistTransactions(transactions.filter((tx) => tx.id !== id));
    },
    [transactions, persistTransactions]
  );

  const toggleMarked = useCallback(
    (id: string) => {
      persistTransactions(
        transactions.map((tx) =>
          tx.id === id ? { ...tx, marked: !tx.marked } : tx
        )
      );
    },
    [transactions, persistTransactions]
  );

  const deleteMarked = useCallback(() => {
    const remaining = transactions.filter((tx) => !tx.marked);
    const removed = transactions.length - remaining.length;
    persistTransactions(remaining);
    return removed;
  }, [transactions, persistTransactions]);

  const addCategory = useCallback(
    (name: string, type: TransactionType, color: string) => {
      const cat: Category = {
        id: generateId(),
        userId,
        name: name.trim(),
        type,
        color,
      };
      persistCategories([...categories, cat]);
    },
    [userId, categories, persistCategories]
  );

  const updateCategory = useCallback(
    (id: string, name: string) => {
      persistCategories(
        categories.map((c) => (c.id === id ? { ...c, name: name.trim() } : c))
      );
    },
    [categories, persistCategories]
  );

  const deleteCategory = useCallback(
    (id: string): string | null => {
      const inUse = transactions.some((tx) => tx.categoryId === id);
      if (inUse) return 'Cannot delete a category that has transactions.';
      persistCategories(categories.filter((c) => c.id !== id));
      return null;
    },
    [transactions, categories, persistCategories]
  );

  const getCategoriesByType = useCallback(
    (type: TransactionType) => categories.filter((c) => c.type === type),
    [categories]
  );

  const displayTransactions = useMemo(
    () =>
      transactions.map((tx) =>
        tx.source === 'wallet'
          ? { ...tx, amount: getDisplayAmount(tx) }
          : tx
      ),
    [transactions, getDisplayAmount]
  );

  const availableMonthKeys = useMemo(
    () => availableMonths(displayTransactions),
    [displayTransactions]
  );

  const filteredTransactions = useMemo(
    () => filterTransactions(displayTransactions, filters),
    [displayTransactions, filters]
  );

  const totals = useMemo(
    () => calcTotals(filteredTransactions),
    [filteredTransactions]
  );

  const monthlyData = useMemo(
    () => groupByMonth(filteredTransactions),
    [filteredTransactions]
  );

  const expenseByCategory = useMemo(
    () => groupByCategory(filteredTransactions, categories, 'expense'),
    [filteredTransactions, categories]
  );

  const incomeByCategory = useMemo(
    () => groupByCategory(filteredTransactions, categories, 'income'),
    [filteredTransactions, categories]
  );

  const balanceOverTime = useMemo(
    () => getBalanceOverTime(filteredTransactions),
    [filteredTransactions]
  );

  const value = useMemo(
    () => ({
      ready,
      storageError,
      transactions: displayTransactions,
      categories,
      filters,
      filteredTransactions,
      totals,
      monthlyData,
      expenseByCategory,
      incomeByCategory,
      balanceOverTime,
      currency,
      currencySymbol,
      usdtPriceBdt,
      wallets,
      cryptoRates,
      setCurrency,
      setUsdtPriceBdt,
      addWallet,
      updateWallet,
      deleteWallet,
      setFilters,
      resetFilters,
      addTransaction,
      addTransactions,
      updateTransaction,
      deleteTransaction,
      toggleMarked,
      deleteMarked,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategoriesByType,
      getDisplayAmount,
      availableMonthKeys,
    }),
    [
      ready,
      storageError,
      displayTransactions,
      categories,
      filters,
      filteredTransactions,
      totals,
      monthlyData,
      expenseByCategory,
      incomeByCategory,
      balanceOverTime,
      currency,
      currencySymbol,
      usdtPriceBdt,
      wallets,
      cryptoRates,
      setCurrency,
      setUsdtPriceBdt,
      addWallet,
      updateWallet,
      deleteWallet,
      setFilters,
      resetFilters,
      addTransaction,
      addTransactions,
      updateTransaction,
      deleteTransaction,
      toggleMarked,
      deleteMarked,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategoriesByType,
      getDisplayAmount,
      availableMonthKeys,
    ]
  );

  return (
    <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>
  );
}

export function useFinance(): FinanceContextValue {
  const ctx = useContext(FinanceContext);
  if (!ctx) throw new Error('useFinance must be used within FinanceProvider');
  return ctx;
}
